import { IServerSideSelectionState, IStatusPanelParams } from "ag-grid-enterprise";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IconButton } from "@mui/material";
import { getSelectedRowsCustom } from "../../../utils/AGGridUtils";

interface CustomStatusPanelParams extends IStatusPanelParams {
    showTotalCount: boolean;
    availablePageSizes: number[];
    enablePageSizeSelector: boolean;
    enableRefreshButton: boolean;
}

const CustomGridFooter: FC<CustomStatusPanelParams> = ({ api, showTotalCount, availablePageSizes, enablePageSizeSelector, enableRefreshButton }) => {
    const [countLoaded, setCountLoaded] = useState(0);
    const [totalElementsCount, setTotalElementsCount] = useState(0);
    const [selectedRowsCount, setSelectedRowsCount] = useState<number>(0);

    useEffect(() => {
        function getRowCount(): number | undefined {
            // const currentCount = api.getModel().getRowCount();
            const currentCount = api.getDisplayedRowCount();

            if (currentCount > 1) {
                return currentCount;
            }

            if (currentCount < 1) {
                return currentCount;
            }

            const nodeData = api.getDisplayedRowAtIndex(0)?.data;

            return nodeData ? currentCount : undefined;
        }

        function updateLoadedRecordsCount() {
            if (api.getGridOption("rowModelType") !== "clientSide") {
                const loadedItemsBuffer = api.getCacheBlockState() ? Object.values<any>(api.getCacheBlockState()).map((e) => e.loadedRowCount) : [];

                if (loadedItemsBuffer?.length) {
                    const loadedItemsCount = loadedItemsBuffer.reduce((a, e) => a + e, 0);
                    setCountLoaded(loadedItemsCount);
                    const isAllRowsSelected = api.getServerSideSelectionState() as IServerSideSelectionState;

                    if (isAllRowsSelected?.selectAll) {
                        const selectedRows = loadedItemsCount - isAllRowsSelected.toggledNodes.length;
                        setSelectedRowsCount(selectedRows);
                    }
                    return;
                }
            }

            setCountLoaded(api.getDisplayedRowCount());
            setTotalElementsCount(api.getGridOption("context")?.totalElementsCount || 0);
        }

        function updateTotalRecordsCount() {
            if (api.getGridOption("rowModelType") === "clientSide") {
                setTotalElementsCount(getRowCount() || 0);
                return;
            }

            setTotalElementsCount(api.getGridOption("context")?.totalElementsCount || 0);
        }

        function updateSelectedRowsCount() {
            const currentSelected = getSelectedRowsCustom(api);
            setSelectedRowsCount(currentSelected?.length || 0);
        }

        if (!api.isDestroyed()) {
            api.addEventListener("modelUpdated", updateTotalRecordsCount);
            api.addEventListener("paginationChanged", updateLoadedRecordsCount);
            // api.addEventListener("rowDataUpdated", updateTotalRecordsCount);
            api.addEventListener("firstDataRendered", updateTotalRecordsCount);
            api.addEventListener("selectionChanged", updateSelectedRowsCount);
        }

        return () => {
            if (api.isDestroyed()) {
                return;
            }
            api.removeEventListener("modelUpdated", updateTotalRecordsCount);
            api.removeEventListener("paginationChanged", updateLoadedRecordsCount);
            // api.removeEventListener("rowDataUpdated", updateTotalRecordsCount);
            api.removeEventListener("firstDataRendered", updateTotalRecordsCount);
            api.removeEventListener("selectionChanged", updateSelectedRowsCount);
        };
    }, [api]);

    const produceFooterMessage = (selectedCount, showTotal) => {
        if (showTotal) {
            return selectedRowsCount ? (
                <FormattedMessage
                    id="generic-pro-grid.footer.message.totals-and-selected-count"
                    defaultMessage={"Currently loaded {countLoaded} records, {selectedCount} selected out of {count} total"}
                    values={{
                        countLoaded: (
                            <span key="ag-status-count-loaded" className="ag-status-name-value-value">
                                {countLoaded}
                            </span>
                        ),
                        count: (
                            <span key="ag-status-total-count" className="ag-status-name-value-value">
                                {totalElementsCount}
                            </span>
                        ),
                        selectedCount: (
                            <span key="ag-status-count-selected" className="ag-status-name-value-value">
                                {selectedRowsCount}
                            </span>
                        ),
                    }}
                />
            ) : (
                <FormattedMessage
                    id="generic-pro-grid.footer.message.totals-count"
                    defaultMessage={"Currently loaded {countLoaded} records out of {count} total"}
                    values={{
                        countLoaded: (
                            <span key="ag-status-count-loaded" className="ag-status-name-value-value">
                                {countLoaded}
                            </span>
                        ),
                        count: (
                            <span key="ag-status-total-count" className="ag-status-name-value-value">
                                {totalElementsCount}
                            </span>
                        ),
                    }}
                />
            );
        }

        return selectedRowsCount ? (
            <FormattedMessage
                id="generic-pro-grid.footer.message.selected-count-without-totals"
                defaultMessage={"Currently loaded {countLoaded} records, {selectedCount} selected"}
                values={{
                    countLoaded: <span className="ag-status-name-value-value">{countLoaded}</span>,
                    selectedCount: <span className="ag-status-name-value-value">{selectedRowsCount}</span>,
                }}
            />
        ) : (
            <FormattedMessage
                id="generic-pro-grid.footer.message.selected-count-without-total"
                defaultMessage={"Currently loaded {countLoaded} records"}
                values={{
                    countLoaded: <span className="ag-status-name-value-value">{countLoaded}</span>,
                }}
            />
        );
    };

    const renderPageSizeSelector = () => {
        return enablePageSizeSelector && api.getGridOption("rowModelType") !== "clientSide" ? (
            <div className="ag-status-name-value">
                <FormattedMessage id="generic-pro-grid.footer.message.page-size" defaultMessage="Page Size: " />
                <select
                    style={{ borderRadius: "3px", margin: "0 5px" }}
                    value={api.getGridOption("cacheBlockSize")}
                    onChange={(e) => {
                        api.setGridOption("cacheBlockSize", Number(e.target.value));
                    }}
                >
                    {availablePageSizes.map((size) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </select>
            </div>
        ) : null;
    };

    const renderRefreshButton = () => {
        return enableRefreshButton && api.getGridOption("rowModelType") !== "clientSide" ? (
            <div style={{ display: "flex" }}>
                <IconButton style={{ cursor: "pointer" }} onClick={() => api.refreshServerSide({ purge: true })} aria-label="Refresh Data">
                    <RefreshIcon sx={(theme) => ({ fontSize: "1.3rem", color: theme.palette.softBlack?.main })} />
                </IconButton>
            </div>
        ) : null;
    };

    return (
        <div className="ag-status-bar ag-status-bar-custom" style={{ border: "none", padding: 0 }}>
            <div className="ag-status-name-value">{produceFooterMessage(selectedRowsCount, showTotalCount)}</div>
            {renderPageSizeSelector()}
            {renderRefreshButton()}
        </div>
    );
};

export default CustomGridFooter;
